import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    SettingOption,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export default class NodeArgument extends BaseFeature {
    constructor() {
        super();
        this.name = 'Node Argument';
        this.id = 'node_argument';

        const settings: Setting[] = [
            {
                id: 'ARGVALUE',
                name: 'Argument Value',
                settingType: SettingType.DYNAMIC,
                required: true,
                helpText: `
##### From parameter
Select from the passed in parameters

##### Setting
Select from a setting create with the Settings library

##### Expression
Write a Tibbo Basic expression that will be pass in as an argument

##### String
Quotes do not need to be added.

Quotes will be escaped for you.

You can create formatted string with parameters substituted in with a $ sign e.g.

\`\`\`
{"key": "$data"}
\`\`\`
where data is a passed in parameter


You can also substitute in a value from a setting with $$ sign e.g.

\`\`\`
{"key": "$$STG"}
\`\`\`
where STG is the setting ID

`,
                default: '',
            },
            {
                id: 'TARGETPARAM',
                name: 'Target Parameter',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const result: SettingOption[] = [];
        if (settingId === 'ARGVALUE') {
            const project: ProjectConfiguration = this.project;
            const ARGTYPE = this.getSettingValue('ARGTYPE');
            const TARGETPARAM = this.getSettingValue('TARGETPARAM');
            const link = project.getParent(this);
            if (ARGTYPE === 'param') {
                const sourceParams = project.getFeature(link.getSettingValue('SOURCE'))?.getSettingValue('PARAMS');
                const targetParams = project.getFeature(link.getSettingValue('TARGET'))?.getSettingValue('PARAMS');
                let targetParamType: TibboDataType = TibboDataType.STRING;
                for (let i = 0; i < targetParams.length; i += 1) {
                    if (targetParams[i].getSettingValue('NAME') === TARGETPARAM) {
                        targetParamType = targetParams[i].getSettingValue('DATATYPE');
                        break;
                    }
                }
                for (let i = 0; i < sourceParams.length; i += 1) {
                    const param = sourceParams[i];
                    const name = param.getSettingValue('NAME');
                    const value = project.convertDataType(name, param.getSettingValue('DATATYPE'), targetParamType);
                    result.push({
                        name,
                        value,
                    });
                }
            }
            if (ARGTYPE === 'setting') {
                const settings = project.getFeature('stg')?.getSettingValue('SETTINGS');
                const targetParams = project.getFeature(link.getSettingValue('TARGET'))?.getSettingValue('PARAMS');
                let targetParamType: TibboDataType = TibboDataType.STRING;
                for (let i = 0; i < targetParams.length; i += 1) {
                    if (targetParams[i].getSettingValue('NAME') === TARGETPARAM) {
                        targetParamType = targetParams[i].getSettingValue('DATATYPE');
                        break;
                    }
                }
                for (let i = 0; i < settings.length; i += 1) {
                    const param = settings[i];
                    const name = param.getSettingValue('NAME');
                    const settingExpression = `stg_get("${param.getSettingValue('SETTINGID')}",0)`;
                    const value = project.convertDataType(
                        settingExpression, TibboDataType.STRING, targetParamType,
                    );
                    result.push({
                        name,
                        value,
                    });
                }
            }
            if (ARGTYPE === 'global') {
                const targetParams = project.getFeature(link.getSettingValue('TARGET'))?.getSettingValue('PARAMS');
                let targetParamType: TibboDataType = TibboDataType.STRING;
                for (let i = 0; i < targetParams.length; i += 1) {
                    if (targetParams[i].getSettingValue('NAME') === TARGETPARAM) {
                        targetParamType = targetParams[i].getSettingValue('DATATYPE');
                        break;
                    }
                }
                const vars = project.getVariables();
                for (let i = 0; i < vars.length; i += 1) {
                    const varItem = vars[i];
                    const name = varItem.getSettingValue('VARNAME');
                    const value = project.convertDataType(name, varItem.getSettingValue('DATATYPE'), targetParamType);
                    result.push({
                        name: `${name} (${varItem.getSettingValue('NAME')})`,
                        value,
                    });
                }
            }
        } else {
            return super.getSettingOptions(settingId);
        }
        return result;
    }


    validate(): Array<ValidationMessage> {
        const project: ProjectConfiguration = this.project;
        const result: ValidationMessage[] = [];
        const ARGVALUE = this.getSettingValue('ARGVALUE');
        const targetParam = this.getSettingValue('TARGETPARAM');
        const regex = /{{s*\$([A-Za-z0-9_]+)s*}}/gm;
        const m = regex.exec(ARGVALUE);
        const parent = project.getParent(this);
        const parentId = parent.getID();
        if (m) {
            const variableName = m[1];
            const projectVariables = project.getVariables();
            let found = false;
            for (let i = 0; i < projectVariables.length; i += 1) {
                if (projectVariables[i].getID() === variableName) {
                    found = true;
                    break;
                }
            }
            const links = project.getFeatureArray('node_link');
            for (let j = 0; j < links.length; j++) {
                const link = links[j];
                if (link.getSettingValue('TARGET_FEATURE') === parentId) {
                    const sourcePort = link.getSettingValue('SOURCE_PORT');
                    const sourceFeature = project.getFeature(link.getSettingValue('SOURCE_FEATURE'));
                    const sourceOutputs = (sourceFeature as BaseNode).getOutputs();
                    for (let k = 0; k < sourceOutputs.length; k++) {
                        const output = sourceOutputs[k].params;
                        if (sourceOutputs[k].portId === sourcePort) {
                            for (let l = 0; l < output.length; l++) {
                                if (output[l].name === variableName) {
                                    found = true;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            if (!found) {
                result.push({
                    message: 'Invalid Variable.',
                    messageType: ValidationMessageType.ERROR,
                    featureId: parent.getID(),
                    settingId: targetParam,
                });
            }
        }
        const ports = parent.getInputs().concat(parent.getOutputs());
        for (let i = 0; i < ports.length; i += 1) {
            const params = ports[i].params;
            for (let j = 0; j < params.length; j += 1) {
                if (params[j].name === targetParam) {
                    if (params[j].dataType !== TibboDataType.STRING
                        && ARGVALUE.length === 0) {
                        result.push({
                            message: 'Parameter cannot be empty.',
                            messageType: ValidationMessageType.ERROR,
                            featureId: parent.getID(),
                            settingId: targetParam,
                        });
                    }
                }
            }
        }
        return result;
    }

    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'TARGETPARAM':
                return SettingState.DISABLED;
            default:
                return SettingState.ENABLED;
        }
    }
}
